import { useEffect, useState } from 'react';
import {
  mdiAccountPlusOutline,
  mdiArrowLeft,
  mdiDotsHorizontal,
} from '@mdi/js';
import {
  Box,
  Button,
  Heading,
  Skeleton,
  Flex,
  useToast,
  Stack,
  Editable,
  EditablePreview,
  EditableInput,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { PortalIcon } from 'features/common/components/PortalIcon';
import { useUpdateOrgGroup } from 'features/user-group/user-group-details/api/useUpdateOrgGroup';
import { GroupDeleteConfirmation } from 'features/user-group/user-group-details/components/GroupDeleteConfirmation';
import { useGroupData } from '../hooks/useGroupData';
import { useOrgGroupMembersList } from 'features/user-group/user-group-tabs/api/useOrgGroupMembersList';
import { UserGroupTabs } from 'features/user-group/user-group-tabs/UserGroupTabs';
import { GroupConfigurationTab } from '../../user-group-tabs/components/GroupConfigurationTab';
import { GroupMembersTab } from 'features/user-group/user-group-tabs/components/GroupMembersTab';
import { openDrawer } from 'features/main/context/drawer/actionCreators';
import { useDrawerStateDispatch } from 'features/main/context';

const GROUP_NAME_DISPLAY_MAX_CHARS = 37;

interface GroupListLocationState {
  page?: number | string;
}

export type IOrgGroupUserDetails = RouteComponentProps<{
  page?: string;
  id: string;
  tab?: string;
}>;

export const GroupListDetails = (props: IOrgGroupUserDetails) => {
  const { tab } = props.match.params;
  let tabContent;
  if (tab === 'configuration') {
    tabContent = <GroupConfigurationTab />;
  } else {
    tabContent = <GroupMembersTab />;
  }
  const [editName, setEditName] = useState<string>('');
  const [editDescription, setEditDescription] = useState<string>('');
  const [showDeleteTeamDialog, setShowDeleteTeamDialog] =
    useState<boolean>(false);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);
  const [_, drawerDispatch] = useDrawerStateDispatch();

  const history = useHistory();
  const toast = useToast();
  const groupId = props.match.params.id;
  const tabParam = props.match.params.tab;
  const pageFromUrl = props.match.params.page;
  const pageFromState = (history.location.state as GroupListLocationState)
    ?.page;
  const page = pageFromUrl ? parseInt(pageFromUrl) : pageFromState || 1;

  const { name, description, orgGroupLoading } = useGroupData(groupId);
  const { data: groupMembersData } =
    useOrgGroupMembersList(groupId, {
      pageSize: 1,
      enabled: showDeleteTeamDialog,
    }) || {};
  const {
    mutate: updateGroup,
    isLoading: updatingNameDescription,
    error: isError,
  } = useUpdateOrgGroup();
  const isSkeletonLoaded =
    (!orgGroupLoading && !showSkeleton) || Boolean(isError);

  const getBackUrl = () => {
    return `/admin/user-groups/page/${page}`;
  };
  const backUrl = getBackUrl();

  useEffect(() => {
    if (!tabParam) {
      history.replace(`/admin/user-groups/${groupId}/members`);
    }
  }, [tabParam, groupId, history]);

  useEffect(() => {
    setEditName(name || '');
    setEditDescription(description || '');
  }, [name, description]);

  //Loading skeleton for 100ms to avoid flickering
  useEffect(() => {
    setShowSkeleton(true);
    const timeout = setTimeout(() => {
      setShowSkeleton(false);
    }, 100);

    return () => clearTimeout(timeout);
  }, [groupId]);

  const updateGroupNameDescription = async (fields: {
    name?: string;
    description?: string;
  }) => {
    if (!groupId) return;
    //validate name length
    if (fields.name !== undefined && fields.name.length < 3) {
      setEditName(name);
      toast({
        status: 'error',
        description:
          "Failed to update group's name. Name must be at least 3 characters.",
      });
      return;
    }
    updateGroup(
      {
        groupId,
        ...fields,
      },
      {
        onSuccess: () => {
          toast({
            status: 'success',
            description: `Group ${
              fields.name ? 'name' : 'description'
            } updated successfully`,
          });
        },
        onError: () => {
          toast({
            status: 'error',
            description: `Failed to update group ${
              fields.name ? 'name' : 'description'
            }`,
          });
        },
      },
    );
  };

  return (
    <Box>
      <Button
        variant='ghost'
        alignItems='center'
        onClick={() => history.push(backUrl)}
        data-testid='back-button'
        size='sm'
      >
        <PortalIcon path={mdiArrowLeft} mr='2' />
        Back to user groups
      </Button>
      <Box m='5' pb='6'>
        <Stack spacing='6'>
          <Flex w='100%' justifyContent='space-between'>
            <Heading
              size='xl'
              mb='6'
              flex={1}
              minW={0}
              maxW='680px'
              overflow='hidden'
              data-testid='group-name-heading'
            >
              <Skeleton isLoaded={isSkeletonLoaded && !updatingNameDescription}>
                <Editable
                  value={editName}
                  className='break-words'
                  placeholder={"Group's Name"}
                  onChange={setEditName}
                  onSubmit={() =>
                    updateGroupNameDescription({ name: editName })
                  }
                >
                  <Tooltip
                    label={editName}
                    placement='top'
                    isDisabled={editName.length <= GROUP_NAME_DISPLAY_MAX_CHARS}
                  >
                    <EditablePreview
                      noOfLines={1}
                      data-testid='team-name-preview'
                    />
                  </Tooltip>
                  <EditableInput
                    maxWidth='100%'
                    data-testid='team-name-input'
                  />
                </Editable>
              </Skeleton>
            </Heading>

            <Skeleton isLoaded={isSkeletonLoaded} flexShrink={0}>
              <Flex
                flexShrink={0}
                alignItems='center'
                gap={2}
                data-testid='group-header-actions'
              >
                <Button
                  leftIcon={<PortalIcon path={mdiAccountPlusOutline} />}
                  onClick={() =>
                    drawerDispatch(
                      openDrawer('add-members-drawer', { groupId }),
                    )
                  }
                >
                  Add users
                </Button>
                <Menu placement='bottom-end'>
                  <MenuButton
                    onClick={(event) => event.stopPropagation()}
                    as={IconButton}
                    size='sm'
                    variant='ghost'
                    isRound={true}
                    border={'1px solid'}
                    borderColor='gray.200'
                    data-testid='team-details-action-button'
                    aria-label='Group actions'
                    icon={<PortalIcon path={mdiDotsHorizontal} />}
                  />
                  <MenuList>
                    <MenuItem
                      onClick={() => {
                        setShowDeleteTeamDialog(true);
                      }}
                    >
                      Delete group
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Flex>
            </Skeleton>
          </Flex>
        </Stack>
        <Box w='xl'>
          <Skeleton isLoaded={isSkeletonLoaded && !updatingNameDescription}>
            <Editable
              value={editDescription}
              className='break-words'
              placeholder="Group's Description"
              onChange={setEditDescription}
              onSubmit={() =>
                updateGroupNameDescription({ description: editDescription })
              }
            >
              <EditablePreview data-testid='team-description-preview' />
              <EditableInput data-testid='team-description-input' />
            </Editable>
          </Skeleton>
        </Box>
      </Box>
      <Skeleton isLoaded={isSkeletonLoaded}>
        <UserGroupTabs groupId={groupId} locationSearch={location.search} />
        {tabContent}
      </Skeleton>

      {showDeleteTeamDialog && (
        <>
          <GroupDeleteConfirmation
            isOpen={showDeleteTeamDialog}
            groupId={groupId}
            groupName={editName}
            memberCount={
              groupMembersData ? groupMembersData.totalRecords : undefined
            }
            onClose={() => setShowDeleteTeamDialog(false)}
            backUrl={backUrl}
          />
        </>
      )}
    </Box>
  );
};
